import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { FaCreditCard, FaSort, FaTags, FaTimes } from 'react-icons/fa';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import Spinner from 'generic/Spinner';
import PaypalButtonPlaceholder from 'generic/Checkout/PaypalButtonPlaceholder';
import DigitalWalletButtonPlaceholder from 'generic/Checkout/DigitalWalletButtonPlaceholder';
import amazonBuy from 'public/amazon-buy.svg';
import afterpayBuy from 'public/checkout-with-afterpay.png';
import { type SalesFunnelData, useFunnelData } from 'utils/funnel-data-context';
import { useAdData } from 'utils/ad-data-context';
import { useOrderData } from 'utils/order-data-context';
import * as gtm from 'utils/gtm';
import * as rudder from 'utils/rudderstack';
import { useHandleViewOrderSummary } from 'utils/checkout-payment';
import { usePaymentRequest, usePayPalExpress } from 'utils/express-payment';
import {
  DEFAULT_PAYPAL_BUTTON_STYLE,
  OrderPaymentMethod,
  BuyFromType,
  CheckoutLocation,
} from 'constant';
import CheckoutErrorModal from 'generic/Checkout/CheckoutErrorModal';
import useIsInViewPort from 'utils/useIsInViewPort';
import { useOnCheckoutClick } from 'utils/checkout-link';
import { buildCssVar } from 'utils/style-override';
import formatCurrency from 'utils/format-currency';
import { useCart } from 'utils/cart-context';

import type { Offers } from 'queries/graphql';

import styles from './styles.module.scss';

const MainButtonTypes = {
  DIGITAL_WALLET: 'digitalWallet',
  PAY_PAL: 'paypal',
  CREDIT_CARD: 'creditCard',
};

export default function StickyBuyButton({
  buyFromType,
  offer,
  countryShippingWhitelist,
  defaultPaymentOption,
}: {
  buyFromType: string;
  offer: Offers;
  countryShippingWhitelist?: string[];
  defaultPaymentOption: string;
}) {
  const router = useRouter();
  const [shouldShowActivityIndicator, setShouldShowActivityIndicator] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState(null);
  const [modalError, setModalError] = useState<any>({});
  const [showOtherOptions, setShowOtherOptions] = useState(false);

  const { adData } = useAdData();
  const {
    orderId: orderDataOrderId,
    setOrderId,
    authKey: orderDataAuthKey,
    setAuthKey,
    setPaymentProcessor,
    setPaymentMethod,
    setCustomerData,
  } = useOrderData();

  const { funnelId, funnelName, isPaypalSupported, apiBaseUrl, upsellUrl, isAfterpaySupported } =
    useFunnelData<SalesFunnelData>();

  const { cartData, setCartItems, cartIsReady } = useCart();
  const cartLineItem = useMemo(
    () => cartData?.data?.lineItems?.find(item => item.offerId === offer?.hashedId),
    [cartData, offer?.hashedId]
  );

  // Change to the new cart item when the offer changes
  useEffect(() => {
    if (offer) {
      setCartItems([{ offerId: offer?.hashedId, quantity: 1 }]);
    }
  }, [offer, setCartItems]);

  const onClick = useCallback(
    paymentMethod => {
      // @ts-expect-error: TODO: Some items missing between Offer and OfferConfig
      gtm.onBeginCheckout([{ ...offer, quantity: cartLineItem.quantity }], paymentMethod);
      gtm.onAddContactInfo({
        cartData,
        paymentMethod,
        checkoutLocation: CheckoutLocation.SALES_PAGE,
      });
      gtm.onAddShippingInfo({
        cartData,
        paymentMethod,
        checkoutLocation: CheckoutLocation.SALES_PAGE,
      });
      gtm.onAddPaymentInfo({
        paymentMethod,
        cartData,
        checkoutLocation: CheckoutLocation.SALES_PAGE,
      });

      // @ts-expect-error: TODO: Some items missing between Offer and OfferConfig
      rudder.onBeginCheckout([{ ...offer, quantity: cartLineItem.quantity }], paymentMethod);
      rudder.onAddContactInfo({
        cartData,
        paymentMethod,
        checkoutLocation: CheckoutLocation.SALES_PAGE,
      });
      rudder.onAddPaymentInfo({
        paymentMethod,
        cartData,
        checkoutLocation: CheckoutLocation.SALES_PAGE,
      });
    },
    [offer, cartData, cartLineItem]
  );

  const { paymentRequest, handlePaymentRequestClick, supportsDigitalWallet } = usePaymentRequest({
    onClick,
    cartData,
    cartIsReady,
    upsellUrl,
    apiBaseUrl,
    funnelId,
    funnelName,
    adData,
    router,
    countryShippingWhitelist,
    setShouldShowActivityIndicator,
    setOrderId,
    setAuthKey,
    setPaymentProcessor,
    setPaymentMethod,
    setFormErrorMessage,
    setModalError,
    setCustomerData,
  });

  const { handleViewOrderSummary, isViewOrderSummaryDisabled } = useHandleViewOrderSummary({
    setShouldShowActivityIndicator,
    modalError,
    apiBaseUrl,
    funnelId,
  });

  const {
    paypalOptions,
    handlePayPalExpressOnClick,
    handlePayPalExpressCreateOrder,
    handlePayPalExpressOnApprove,
    handlePayPalOnShippingChange,
    handlePayPalOnError,
    handlePayPalOnCancel,
  } = usePayPalExpress({
    setShouldShowActivityIndicator,
    onClick,
    setOrderId,
    setAuthKey,
    setPaymentProcessor,
    setPaymentMethod,
    apiBaseUrl,
    cartData,
    cartIsReady,
    funnelId,
    adData,
    funnelName,
    setCustomerData,
    orderDataOrderId,
    orderDataAuthKey,
    upsellUrl,
    router,
    countryShippingWhitelist,
    setFormErrorMessage,
  });

  const onOtherClick = useCallback(() => {
    setShowOtherOptions(true);
  }, [setShowOtherOptions]);

  const onOtherClose = useCallback(() => {
    setShowOtherOptions(false);
  }, [setShowOtherOptions]);

  const onAmazonClick = useCallback(() => {
    gtm.onExternalCheckout(
      // @ts-expect-error: TODO: Some items missing between Offer and OfferConfig
      { ...offer, quantity: cartLineItem.quantity },
      BuyFromType.AMAZON,
      offer?.amazonPrice
    );
    rudder.onExternalCheckout(
      // @ts-expect-error: TODO: Some items missing between Offer and OfferConfig
      { ...offer, quantity: cartLineItem.quantity },
      BuyFromType.AMAZON,
      offer?.amazonPrice
    );
    window.open(offer?.amazonLink, '_blank', 'noopener');
  }, [offer, cartLineItem?.quantity]);

  const getCreditCardClickHandler = useOnCheckoutClick({ setShouldShowActivityIndicator });

  const getAfterpayClickHandler = useOnCheckoutClick({
    setShouldShowActivityIndicator,
    paymentMethod: OrderPaymentMethod.AFTERPAY,
  });

  const paymentRequestButton = useMemo(
    () => (
      <DigitalWalletButtonPlaceholder>
        {paymentRequest && (
          <PaymentRequestButtonElement
            // Apple's Human Interface Guidelines for Apple Pay disallow disabling this button:
            // https://github.com/stripe-archive/react-stripe-elements/issues/203#issuecomment-392029737
            // Instead, we will handle disabled state in onClick.
            options={{
              paymentRequest,
              style: {
                paymentRequestButton: {
                  height: '55px',
                },
              },
            }}
            onClick={handlePaymentRequestClick}
          />
        )}
      </DigitalWalletButtonPlaceholder>
    ),
    [paymentRequest, handlePaymentRequestClick]
  );

  const payPalButton = useMemo(
    () => (
      // we want to load the PayPay JS SDK before the user selects PayPal so the button renders ASAP
      <div className={styles.paypal_wrapper}>
        <PaypalButtonPlaceholder>
          <PayPalScriptProvider options={paypalOptions}>
            <PayPalButtons
              onClick={handlePayPalExpressOnClick}
              createOrder={handlePayPalExpressCreateOrder}
              onApprove={handlePayPalExpressOnApprove}
              onShippingChange={handlePayPalOnShippingChange}
              onError={handlePayPalOnError}
              onCancel={handlePayPalOnCancel}
              style={DEFAULT_PAYPAL_BUTTON_STYLE}
            />
          </PayPalScriptProvider>
        </PaypalButtonPlaceholder>
      </div>
    ),
    [
      handlePayPalExpressOnClick,
      handlePayPalExpressCreateOrder,
      handlePayPalExpressOnApprove,
      handlePayPalOnShippingChange,
      handlePayPalOnError,
      handlePayPalOnCancel,
      paypalOptions,
    ]
  );

  const creditCardButton = (
    <button
      disabled={!cartIsReady}
      className={styles.credit_btn}
      onClick={getCreditCardClickHandler([cartLineItem])}
    >
      Credit Card
    </button>
  );

  const afterpayButton = (
    <button
      disabled={!cartIsReady}
      onClick={getAfterpayClickHandler([cartLineItem])}
      className={`${styles.afterpay_btn} ${styles.placeholder}`}
    >
      <Image src={afterpayBuy} width={400} height={55} alt="afterpay" />
    </button>
  );

  const isFooterInView = useIsInViewPort('footer');
  const containerRef = useRef<any>(null);

  // Handle payment option for the main button

  const [mainButtonType, setMainButtonType] = useState<string>();
  const [otherButtonColor, setOtherButtonColor] = useState<string>();

  useEffect(() => {
    if (defaultPaymentOption === MainButtonTypes.DIGITAL_WALLET && paymentRequestButton) {
      setMainButtonType(MainButtonTypes.DIGITAL_WALLET);
      setOtherButtonColor('#000');
    } else if (defaultPaymentOption === MainButtonTypes.PAY_PAL && payPalButton) {
      setMainButtonType(MainButtonTypes.PAY_PAL);
      setOtherButtonColor('#ffc439');
    } else {
      setMainButtonType(MainButtonTypes.CREDIT_CARD);
      setOtherButtonColor('var(--button-color)');
    }
  }, [
    supportsDigitalWallet,
    isPaypalSupported,
    setMainButtonType,
    defaultPaymentOption,
    payPalButton,
    paymentRequestButton,
  ]);

  return (
    <div className={`root ${styles.main}`} ref={containerRef}>
      <style jsx global>{`
        .root {
          ${buildCssVar('--other-button-color', otherButtonColor, 'var(--primary-color)')}
        }
        body {
          overflow: ${showOtherOptions ? 'hidden' : 'scroll'};
        }
        #sticky-banner {
          visibility: ${showOtherOptions ? 'hidden' : 'visible'};
        }
        footer {
          padding-bottom: ${isFooterInView
            ? `${containerRef?.current?.clientHeight + 10}px`
            : `10px`} !important;
        }
      `}</style>

      {shouldShowActivityIndicator && <Spinner />}

      <div className={styles.container_wrapper}>
        <div className={styles.sec_header}>
          {buyFromType === BuyFromType.AMAZON && (
            <>
              <h2>Buy now</h2>
              <p>${offer?.amazonPrice}</p>
            </>
          )}
          {buyFromType === BuyFromType.VELOCITY && (
            <>
              <h2>Pay with</h2>
              <p>
                {!cartIsReady && 'Calculating...'}
                {cartIsReady && (
                  <>
                    {cartData?.data.discountCode && (
                      <span className={styles.discount_code}>
                        <FaTags className={styles.tags_icon} />
                        {cartData.data.discountCode}
                      </span>
                    )}{' '}
                    {formatCurrency(cartData?.data.total)}
                  </>
                )}
              </p>
            </>
          )}
        </div>
        <div className={styles.btn_wrapper}>
          {/* Amazon buy button */}
          {buyFromType === BuyFromType.AMAZON && (
            <button
              onClick={onAmazonClick}
              className={`${styles.amazon_btn} ${styles.placeholder}`}
            >
              <Image src={amazonBuy} width={133} height={40} alt="amazon" sizes="133px" />
            </button>
          )}

          {/* Main buy button */}
          {/* Payment request button errors if shown twice, so don't render the main buy button while the other modal is open */}
          {buyFromType === BuyFromType.VELOCITY && !showOtherOptions && (
            <>
              {mainButtonType === MainButtonTypes.DIGITAL_WALLET && paymentRequestButton}
              {mainButtonType === MainButtonTypes.PAY_PAL && payPalButton}
              {mainButtonType === MainButtonTypes.CREDIT_CARD && creditCardButton}
            </>
          )}

          {/* Other button */}
          {buyFromType === BuyFromType.VELOCITY && (
            <button onClick={onOtherClick} className={styles.other_btn}>
              <p>Other</p>
              <FaCreditCard />
              <FaSort />
            </button>
          )}
        </div>
        {/* Other options modal */}
        {showOtherOptions && (
          <div className={styles.modal_wrapper}>
            <div className={styles.other_options_modal}>
              <button onClick={onOtherClose} className={styles.modal_close_btn}>
                <FaTimes />
              </button>
              <div className={styles.other_modal_body}>
                <div className={styles.other_modal_header}>
                  <h2>Pay with</h2>
                  <p>
                    {!cartIsReady && 'Calculating...'}
                    {cartIsReady && (
                      <>
                        {cartData?.data.discountCode && (
                          <span className={styles.discount_code}>
                            <FaTags className={styles.tags_icon} />
                            {cartData.data.discountCode}
                          </span>
                        )}{' '}
                        {formatCurrency(cartData?.data.total)}
                      </>
                    )}
                  </p>
                </div>
                <div className={styles.modal_btn_wrapper}>
                  {isPaypalSupported && payPalButton}
                  {supportsDigitalWallet && paymentRequestButton}
                  {isAfterpaySupported && afterpayButton}
                  {creditCardButton}
                </div>
              </div>
            </div>
          </div>
        )}
        {modalError?.message && (
          <CheckoutErrorModal
            message={modalError.message}
            handleViewOrderSummary={handleViewOrderSummary}
            isViewOrderSummaryDisabled={isViewOrderSummaryDisabled}
          />
        )}
        {formErrorMessage && <p className={styles.invalid_msg}>{formErrorMessage}</p>}
      </div>
    </div>
  );
}
